import { useParams } from 'react-router-dom';
import useTrafficPattern from '@/hooks/useTrafficPattern.js';
import TrafficBoard from '@/components/traffic/TrafficBoard.jsx';
import ConversationMode from '@/components/traffic/ConversationMode.jsx';
import { Button } from '@/components/ui/button.jsx';
import { RefreshCw } from 'lucide-react';

function SkeletonRow() {
  return (
    <div className="rounded-md border border-border bg-surface p-3 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="h-2 w-2 rounded-full bg-border" />
        <div className="h-4 w-48 rounded bg-border" />
        <div className="ml-auto h-3 w-20 rounded bg-border/60" />
      </div>
      <div className="mt-2 ml-5 h-3 w-64 rounded bg-border/60" />
    </div>
  );
}

function PatternSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 md:p-6 animate-pulse">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => <div key={i} className="h-6 w-16 rounded-full bg-border" />)}
      </div>
      {[0, 1].map((i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-center justify-between border-b border-border pb-1.5">
            <div className="h-3 w-20 rounded bg-border" />
            <div className="h-3 w-4 rounded bg-border/60" />
          </div>
          <div className="space-y-2">
            <SkeletonRow />
            <SkeletonRow />
            {i === 0 ? <SkeletonRow /> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TrafficPattern() {
  const { contactId } = useParams();
  const { data, error, loading, refresh, groupBy, mutateGroupBy, filters, mutateFilters, stageFilter, mutateStageFilter } = useTrafficPattern();

  if (loading && !data) {
    return <PatternSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="mx-auto max-w-md p-4 text-center">
        <p className="rounded-md border border-accent/40 bg-accent-soft px-4 py-3 text-sm text-accent">
          Couldn't load the Traffic Pattern.
          <br />
          <span className="font-mono text-xs">{error.message || error.code || 'unknown'}</span>
        </p>
        <Button onClick={refresh} variant="outline" className="mt-4">
          <RefreshCw size={16} strokeWidth={1.5} />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <TrafficBoard
        data={data}
        onMutate={refresh}
        groupBy={groupBy}
        onGroupByChange={mutateGroupBy}
        filters={filters}
        onFiltersChange={mutateFilters}
        stageFilter={stageFilter}
        onStageFilterChange={mutateStageFilter}
      />
      {contactId && <ConversationMode onRefreshPattern={refresh} />}
    </>
  );
}
